import { useEffect } from 'react'
import Container from '../../components/Container/Container'
import Breadcrumbs from '../../components/ui/Breadcrumbs/Breadcrumbs'
import ProductGallery from '../../components/ui/ProductGallery/ProductGallery'
import ProductInfo from '../../components/ui/ProductInfo/ProductInfo'
import ProductPageSkeleton from '../../components/ui/ProductPageSkeleton/ProductPageSkeleton'
import SliderProductsSkeleton from '../../components/ui/SliderProductsSkeleton/SliderProductsSkeleton'
import SliderProducts from '../../components/section/SliderProducts/SliderProducts'
import s from './ProductPage.module.css'
import { useParams } from 'react-router-dom'
import { useImagePreload } from '../../hooks/useImagePreload'
import { useProducts } from '../../hooks/useProducts'
import { usePageMeta } from '../../hooks/usePageMeta'
import { analytics, productToEventItem } from '../../analytics'
import { CURRENCY } from '../../analytics/config'
import JsonLd from '../../components/ui/JsonLd/JsonLd'
import { productJsonLd } from '../../utils/jsonLd'
import {
  HOME_DESCRIPTION,
  PRODUCT_NOT_FOUND_META,
  ROBOTS_NOINDEX,
  productCanonicalPath,
  productDescription,
  productTitle,
} from '../../utils/pageMeta'

export default function ProductPage() {
  const { slug } = useParams()
  const { product, relatedProducts, loadingProduct, loadingRelated, error } = useProducts({
    productSlug: slug,
  })
  usePageMeta(
    product
      ? {
          title: productTitle(product),
          description: productDescription(product),
          path: productCanonicalPath(product.slug),
        }
      : {
          title: loadingProduct ? 'Seaters Coffee' : PRODUCT_NOT_FOUND_META.title,
          description: loadingProduct ? HOME_DESCRIPTION : PRODUCT_NOT_FOUND_META.description,
          path: slug ? productCanonicalPath(slug) : '/',
          robots: ROBOTS_NOINDEX,
        },
  )
  useImagePreload(product?.mainPictureBig || product?.mainPicture)
  // Track ViewContent when product loads
  useEffect(() => {
    if (product) {
      const item = productToEventItem(product)
      analytics.track('view_item', {
        items: [item],
        value: item.price,
        currency: CURRENCY,
      })
    }
  }, [product])

  // Loading state
  if (loadingProduct) {
    return (
      <Container>
        <Breadcrumbs />
        <ProductPageSkeleton />
      </Container>
    )
  }

  // Error state
  if (error || !product) {
    return (
      <Container>
        <Breadcrumbs />
        <div className={s.errorContainer}>
          <h1 className={s.errorTitle}>Товар не знайдено</h1>
          <p className={s.errorMessage}>{error || 'Запитаний товар не існує або був видалений.'}</p>
        </div>
      </Container>
    )
  }

  // Get all product images
  const productImages = [
    product.picture1,
    product.picture2,
    product.picture3,
    product.picture4,
    product.picture5,
  ].filter(Boolean)

  // Custom names for breadcrumbs
  const breadcrumbNames = {
    [slug!]: product.name, // Використовуємо ID як ключ і назву товару як значення
  }

  // Отримуємо назву категорії для breadcrumbs
  const categoryName = product.categoryExternalName
  const categoryInternalName = product.categoryInternalName

  return (
    <Container>
      <JsonLd data={productJsonLd(product)} />
      <Breadcrumbs
        customNames={breadcrumbNames}
        categoryName={categoryName}
        categoryInternalName={categoryInternalName}
      />
      <div className={s.productPage}>
        <div className={s.productContent}>
          <div className={s.gallerySection}>
            <ProductGallery
              mainPicture={product.mainPicture}
              mainPictureBig={product.mainPictureBig}
              images={productImages}
              productName={product.name}
              tags={product.tags && Array.isArray(product.tags) ? product.tags : []}
            />
          </div>

          <div className={s.infoSection}>
            <ProductInfo product={product} />
          </div>
        </div>

        {/* Related Products Section */}
        <div className={s.relatedSection}>
          {loadingRelated ? (
            <SliderProductsSkeleton title="Схожі товари" itemCount={3} />
          ) : (
            relatedProducts.length > 0 && (
              <SliderProducts category="related" products={relatedProducts} />
            )
          )}
        </div>
      </div>
    </Container>
  )
}
